<!-- eslint-disable vue/prop-name-casing -->
<script setup>
import { omit, orderBy } from 'lodash-es';
import { computed, onMounted } from 'vue';
import { useModal } from 'vue-final-modal';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '~/auth/stores/auth.store';
import CustomViewForm from '~/common/components/organisms/custom-views/custom-view-form.vue';
import HawkDeletePopup from '~/common/components/organisms/hawk-delete-popup.vue';
import { useCustomViewStore } from '~/common/stores/custom-view.store.js';

const props = defineProps({
  payload: {
    type: Object,
    default: () => { },
  },
  selected_view: {
    type: String,
    default: null,
  },
  show_default: {
    type: Boolean,
    default: false,
  },
  enable_pinning: {
    type: Boolean,
    default: false,
  },
  is_mounted: {
    type: Boolean,
    default: false,
  },
  personal_views: {
    type: Boolean,
    default: false,
  },
  store_key: {
    type: String,
    default: undefined,
  },
  wrapper_class: {
    type: String,
    default: 'absolute left-12',
  },
});

const emit = defineEmits(['selectedView', 'deletedView', 'closeSaveView', 'updatedView', 'createdView', 'customViewsInstance']);

const custom_view_store = useCustomViewStore(props.store_key || undefined);
const route = useRoute();
const router = useRouter();
const auth_store = useAuthStore();
const $t = inject('$t');
const is_pinning = ref(false);

const form$ = ref(null);

const filtered_views = computed(() => (custom_view_store.views || [])?.filter(view =>
  (!props.payload.service || view.service === props.payload.service)
  && (!props.payload.feature || view.feature === props.payload.feature)
  && (!props.payload.resource_type || view.resource_type === props.payload.resource_type)
  && (!props.payload.resource_id || view.resource_id === props.payload.resource_id)),
);
const view_items = computed(() => [
  ...(props.show_default
    ? [{
        value: 'default',
        label: $t('Default'),
        default: false,
        user_default: false,
      }]
    : []),
  ...(orderBy(filtered_views.value, 'order_index')?.map(view => ({
    value: view.uid,
    label: view.name,
    default: view.default,
    user_default: view.user_default,
  })) || []),
]);
const pinned_view = computed(() => filtered_views.value.find(view => view.user_default) || (props.personal_views ? undefined : filtered_views.value.find(view => view.default)));
const personal_access_payload = computed(() => props.personal_views ? { private: true, members: [{ uid: auth_store.logged_in_user_id, member: true }] } : {});

onMounted(async () => {
  await custom_view_store.set_custom_views({ query: props.payload }, { force_update: true });
  if (props.selected_view === 'default' && !props.is_mounted && props.enable_pinning && pinned_view.value?.uid) {
    form$.value.el$('custom_view').load(pinned_view.value.uid);
    emit('selectedView', pinned_view.value.uid);
  }
  else if (props.selected_view) {
    form$.value.el$('custom_view').load(props.selected_view);
  }
  emit('customViewsInstance', {
    openViewPopup,
    updateCustomView,
  });
});

const custom_view_popup = useModal({
  component: CustomViewForm,
});

const { open: openDeletePopup, close: closeDeletePopup, patchOptions } = useModal({
  component: HawkDeletePopup,
});

function openViewPopup() {
  custom_view_popup.patchOptions({
    attrs: {
      onClose() {
        custom_view_popup.close();
      },
      show_access: !props.personal_views,
      on_submit: async (data) => {
        try {
          const payload = {
            ...props.payload,
            ...data,
            ...personal_access_payload.value,
            organization: auth_store.current_organization?.uid,
            asset: route.params.asset_id,
          };
          const view = await custom_view_store.create_view(payload);
          emit('createdView');
          if (view?.uid && ['terra', 'dms'].includes(props.payload.service)) {
            router.replace({ query: { ...route.query, view_uid: view.uid } });
            form$.value.el$('custom_view').load(view.uid);
            emit('closeSaveView');
          }
        }
        catch (err) {
          logger.error(err);
        }
      },
    },
  });
  custom_view_popup.open();
}
function editViewPopup(view_uid) {
  const custom_view = custom_view_store.views.find(view => view.uid === view_uid);
  custom_view_popup.patchOptions({
    attrs: {
      onClose() {
        custom_view_popup.close();
      },
      onClosed() {
        const attrs = omit(custom_view_popup.options.attrs, ['custom_view']); // reset attrs
        custom_view_popup.options.attrs = attrs;
      },
      custom_view,
      show_access: !props.personal_views,
      on_submit: async (data) => {
        await custom_view_store.update_view(
          {
            ...props.payload,
            ...data,
            ...personal_access_payload.value,
            organization: auth_store.current_organization?.uid,
            asset: route.params.asset_id,

          },
          view_uid,
        );
        emit('updatedView');
      },
    },
  });
  custom_view_popup.open();
}
function deleteView(view) {
  patchOptions({
    attrs: {
      header: $t('Delete View'),
      content: `${$t('Are you sure you want to delete the view')} ${view.label}?`,
      onClose() {
        closeDeletePopup();
      },
      confirm: async () => {
        await custom_view_store.delete_view(view.value);
        emit('deletedView', view.value);
        closeDeletePopup();
      },
    },
  });
  openDeletePopup();
}
async function updateCustomView(data) {
  const custom_view = custom_view_store.views.find(view => view.uid === data.uid);
  await custom_view_store.update_view(
    {
      ...custom_view,
      ...data.payload,
      organization: auth_store.current_organization?.uid,
      asset: route.params.asset_id,
    },
    data.uid,
  );
};
async function updateDefaultView(uid, payload = {}) {
  try {
    is_pinning.value = true;
    // if (pinned_view.value?.uid)
    //   custom_view_store.views_map[pinned_view.value.uid].default = false;
    // custom_view_store.views_map[uid].default = value;
    if (uid !== pinned_view.value?.uid && (payload.default || payload.user_default))
      emit('selectedView', uid, 'Pinned');

    const custom_view = custom_view_store.views.find(view => view.uid === uid);
    if (custom_view) {
      await custom_view_store.update_view(
        {
          ...custom_view,
          ...payload,
        },
        uid,
      );
      await custom_view_store.set_custom_views({ query: props.payload }, { forceUpdate: true });
    }
    is_pinning.value = false;
  }
  catch (error) {
    is_pinning.value = false;
    logger.error(error);
  }
}

function selectViewHandler(option) {
  emit('selectedView', option);
}
</script>

<template>
  <div :class="wrapper_class">
    <Vueform ref="form$" size="sm" :float-placeholders="false" :display-errors="false">
      <SelectElement
        name="custom_view" :native="false" :search="true" :can-clear="false" :items="view_items"
        :truncate="true" :can-deselect="false" :add-classes="{
          SelectElement: {
            select: {
              dropdown: ['scrollbar'],
            },
          },
        }" class="min-w-[14rem]" @select="selectViewHandler"
      >
        <template v-if="enable_pinning" #single-label="{ value }">
          <div
            class="flex items-center justify-between h-full absolute left-0 top-0 pointer-events-auto bg-transparent leading-snug px-3 w-full"
          >
            <HawkText :content="value.label" :length="20" placement="top" class="text-sm" />
            <div>
              <IconHawkLoadingCircle v-if="is_pinning" class="animate-spin w-4 h-4 mr-4" />
              <template v-else-if="value.value === pinned_view?.uid">
                <IconHawkPinnedOneBordered v-if="pinned_view.user_default" class="w-4 h-4 mr-4" />
                <IconHawkPinnedOne v-else class="w-4 h-4 mr-4" />
              </template>
            </div>
          </div>
        </template>
        <template #before-list>
          <p class="flex items-center px-3 text-sm py-1.5 hover:bg-gray-100 font-medium" @click="openViewPopup">
            <IconHawkPlus class="w-4 h-4 mr-1" /> {{ $t('Create view') }}
          </p>
        </template>
        <template #option="{ option }">
          <div class="flex justify-between w-full group gap-2">
            <div class="flex items-center gap-2">
              <HawkText :content="option.label" placement="top" :length="enable_pinning ? 14 : 20" class="text-sm" />
              <span v-if="option.value !== 'default' && enable_pinning">
                <template v-if="option.value === pinned_view?.uid">
                  <IconHawkLoadingCircle v-if="is_pinning" class="animate-spin w-4 h-4" />
                  <IconHawkPinnedOneBordered
                    v-else-if="pinned_view.user_default" v-tippy="{ content: $t('Pinned for you'), placement: 'auto' }"
                    class="w-4 h-4"
                    @click.stop="updateDefaultView(option.value, { user_default: false, default: !personal_views })"
                  />
                  <IconHawkPinnedOne
                    v-else v-tippy="{ content: $t('Pinned for everyone'), placement: 'auto' }"
                    class="w-4 h-4"
                    @click.stop="updateDefaultView(option.value, { user_default: false, default: false })"
                  />
                </template>
                <IconHawkPinTwoInactive

                  v-else v-tippy="{ content: $t('Not pinned'), placement: 'auto' }"
                  class="w-4 h-4 invisible group-hover:visible"
                  @click.stop="updateDefaultView(option.value, { user_default: true, default: false })"
                />

                <!-- <IconHawkPinnedOne v-if="option.value === pinned_view?.uid" class="w-4 h-4"
                  @click.stop="updateDefaultView(option.value, false)" />
                <IconHawkPinTwoInactive v-else class="w-4 h-4 invisible group-hover:visible"
                  @click.stop="updateDefaultView(option.value, true)" /> -->
              </span>
            </div>
            <div v-if="option.value !== 'default'" class="flex items-center gap-2">
              <IconHawkEditOne
                class="w-4 h-4 invisible group-hover:visible"
                @click.stop="editViewPopup(option.value)"
              />
              <IconHawkDeletePopup class="w-4 h-4 invisible group-hover:visible" @click.stop="deleteView(option)" />
            </div>
          </div>
        </template>
      </SelectElement>
    </vueform>
  </div>
</template>
